import sanitizeHtml, { type Attributes, type Tag } from 'sanitize-html';

import { Category } from '../../types';
import { isDevOrTest } from '../environment';

export const allowVulnerableTags = true;

// Production traffic goes to a dedicated Cloudflare Worker on its own subdomain so video
// Range requests don't share Reader's per-host browser connection budget — that contention
// caused 30–60s "Queued" delays in Safari (RW-44084). In dev/test we keep hitting the local
// Django proxy via the inherited server base URL.
const TWITTER_VIDEO_PROXY_BASE_URL = isDevOrTest
  ? (
      (process.env.VITE_SERVER_BASE_URL || process.env.EXPO_SERVER_BASE_URL)?.replace(/\/+$/, '') ??
      'https://readwise.io'
    )
  : 'https://video-proxy.readwise.io';

export const defaultAllowedClasses: { [key: string]: string[]; } = {
  article: ['rw-embedded-tweet', 'rw-embed', 'rw-discussion-post', 'rw-discussion-post-data', 'rw-discussion-post-content'],
  hr: ['twitter-thread-delimiter'],
  p: ['rw-outer-content', 'rw-ai', 'rw-hide'],
  span: ['rw-ai', 'rw-ai-explainer', 'rw-ai-highlighted', 'rw-podcast-timestamp'],
  li: ['rw-ai'],
  div: ['rw-digest-thread-wrapper', 'rw-embed-wrapper', 'rw-soundcloud-wrapper', 'rw-embed-title', 'rw-podcast-timestamp'],
  img: ['rw-embed-img'],
  figure: ['rw-author-byline'],
};

export const defaultAllowedSchemesByTag = {
  img: sanitizeHtml.defaults.allowedSchemes.concat(['data']),
};

// Inline SVG is the only foreign content we keep, and sanitize-html treats a literal
// ['*'] as a REAL attribute wildcard — which previously let `<svg onmouseover=...>` /
// `<path onclick=...>` survive sanitization and execute in the same-origin Reader
// content DOM (stored XSS). Use explicit allowlists of safe presentation/structural
// attributes instead. All event-handler (on*) attributes are intentionally excluded,
// and `style` is only allowed on <svg>, filtered down to sizing by defaultAllowedStyles.
const svgPresentationAttributes = [
  'fill', 'fill-opacity', 'fill-rule',
  'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin',
  'stroke-dasharray', 'stroke-dashoffset', 'stroke-miterlimit', 'stroke-opacity',
  'opacity', 'color', 'clip-rule', 'vector-effect', 'transform',
  'class', 'id',
];
const svgAllowedAttributes = [
  'xmlns', 'xmlns:xlink', 'viewbox', 'preserveaspectratio', 'version',
  'width', 'height', 'x', 'y', 'style',
  ...svgPresentationAttributes,
  'role', 'focusable',
  'aria-hidden', 'aria-label', 'aria-labelledby',
];
const pathAllowedAttributes = ['d', 'pathlength', ...svgPresentationAttributes];
// <g> carries MathJax's y-flip and per-glyph translate/scale transforms; <rect> draws its
// fraction bars and radical rules. Without them a formula renders as an empty box.
const groupAllowedAttributes = Array.from(svgPresentationAttributes);
const rectAllowedAttributes = ['x', 'y', 'width', 'height', 'rx', 'ry', ...svgPresentationAttributes];

// sanitize-html only filters styles for tags listed here; every other tag keeps whatever
// `style` its variant allows. On <svg> we accept sizing and nothing else — enough for the
// em-based inline sizing MathJax formulas need, with no room for a positioning/paint payload.
// Bounded on purpose: untrusted content shouldn't be able to size an svg past the column.
// MathJax's widest real formulas sit well under 100em, so these keep every formula while
// ruling out the `999em`-style values that recreate the oversized layout this PR fixes.
const SVG_LENGTH_REGEXES = [
  /^\d{1,2}(\.\d{1,3})?(em|ex)$/,
  /^\d{1,3}(\.\d{1,3})?px$/,
  /^(100|\d{1,2}(\.\d{1,3})?)%$/,
];
// Depth below the baseline — a fraction of a line, and negative by nature.
const SVG_VERTICAL_ALIGN_REGEXES = [/^-?\d(\.\d{1,3})?(em|ex|px)$/];
// svg width/height attributes may also be unitless (user units, i.e. px).
const SVG_UNITLESS_LENGTH_REGEX = /^\d{1,3}(\.\d{1,3})?$/;
// Anchored to a declaration boundary so `max-width` doesn't read as a width.
const SIZING_DECLARATION_REGEX = /(?:^|;)\s*(?:width|height)\s*:\s*([^;]+)/g;

export const defaultAllowedStyles = {
  svg: {
    width: SVG_LENGTH_REGEXES,
    height: SVG_LENGTH_REGEXES,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'vertical-align': SVG_VERTICAL_ALIGN_REGEXES,
  },
};

function isBoundedSvgLength(value: string | undefined, allowUnitless = false) {
  const trimmed = value?.trim();
  if (!trimmed) {
    return false;
  }
  return (
    (allowUnitless && SVG_UNITLESS_LENGTH_REGEX.test(trimmed)) ||
    SVG_LENGTH_REGEXES.some((regex) => regex.test(trimmed))
  );
}

// Whether an svg sizes itself with a value that will still be there after sanitization.
// transformTags run before both the style filter and the attribute allowlist, so anything
// that gets dropped later (`width: calc(100vw)`) or is absurd (`width="1000000000"`) must
// not count — otherwise the svg escapes the viewBox fallback below and ends up
// dimensionless or oversized. Reusing SVG_LENGTH_REGEXES keeps the two in lockstep.
function isSelfSized(attribs: Attributes) {
  if (isBoundedSvgLength(attribs.width, true) || isBoundedSvgLength(attribs.height, true)) {
    return true;
  }
  return Array.from((attribs.style ?? '').matchAll(SIZING_DECLARATION_REGEX))
    .some(([, value]) => isBoundedSvgLength(value));
}

export function getDefaultAllowedTags() {
  return sanitizeHtml.defaults.allowedTags.concat([
    'img',
    'image',
    'video',
    'source',
    'svg',
    'path',
    'g',
    'rect',
    'picture',
    'center',
    'embed',
    'details',
    'summary',
  ]);
}

export function getDefaultAllowedAttributes(
  attributesAllowedOnAnyTag: string[],
  allowImageStyles: boolean,
) {
  return {
    a: ['href', 'name', 'target', 'rel', 'data-rw-button'],
    img: ['src', 'referrerpolicy', ...(allowImageStyles ? ['style', 'width', 'height', 'class'] : [])],
    image: ['xlink:href', ...(allowImageStyles ? ['style', 'width', 'height', 'class'] : [])],
    source: ['src', 'type', 'srcset', 'media'],
    video: ['controls', 'src', 'preload', 'playsinline', 'poster'],
    embed: ['src'],
    path: pathAllowedAttributes,
    svg: svgAllowedAttributes,
    g: groupAllowedAttributes,
    rect: rectAllowedAttributes,
    ol: ['start'],
    th: ['rowspan', 'colspan'],
    td: ['rowspan', 'colspan'],
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '*': attributesAllowedOnAnyTag,
  };
}

export function getDefaultTransformTags(allowImageStyles: boolean, category: Category) {
  return {
    source: (tagName: string, attribs: Attributes): Tag => {
      // Proxy Twitter MP4 video sources through our server to avoid CORS issues.
      // HLS (.m3u8) sources are removed entirely via exclusiveFilter in the sanitize options
      // (Chrome returns "maybe" for canPlayType('application/x-mpegURL') and tries them anyway).
      const srcPath = attribs.src?.split('?')[0] ?? '';
      if (srcPath.includes('video.twimg.com') && !srcPath.endsWith('.m3u8')) {
        attribs.src = `${TWITTER_VIDEO_PROXY_BASE_URL}/reader/video-proxy/?url=${encodeURIComponent(attribs.src)}`;
      }
      return {
        tagName,
        attribs,
      };
    },
    video: (tagName: string, attribs: Attributes): Tag => {
      // `preload="metadata"` (not `"none"`): on iOS Safari/WKWebView, `play()` against a
      // `preload="none"` <video> does not actually trigger media loading on the first
      // user tap — the play button flips to pause and AVFoundation sits idle until the
      // user pauses and taps play again. Metadata-only preload keeps bandwidth small
      // (~tens of KB per video) while giving the element the readyState needed for
      // `play()` to kick off the actual fetch on iOS.
      attribs.preload = 'metadata';
      // Required for inline video playback on iOS Safari/WKWebView
      attribs.playsinline = '';
      return {
        tagName,
        attribs,
      };
    },
    embed: (tagName: string, attribs: Attributes): Tag => {
      const youtubeDomains = ['youtube.com', 'youtu.be', 'm.youtube.com'];
      const isYoutube = youtubeDomains.some((domain) => attribs.src?.includes(domain));

      if (isYoutube) {
        const url = new URL(attribs.src);
        const params = new URLSearchParams(url.search);
        params.delete('autoplay');
        params.delete('playsinline');
        url.search = params.toString();
        attribs.src = url.toString();
      }

      return {
        tagName,
        attribs,
      };
    },
    svg(tagName: string, attribs: Attributes): Tag {
      // An svg that sizes itself keeps its own sizing. Inline MathJax formulas arrive with
      // em sizing in `style`; stamping the viewBox on as px would blow one up to a
      // ~1200x960px box mid-paragraph. One declared dimension is enough — the browser
      // derives the other from the viewBox aspect ratio.
      if (isSelfSized(attribs)) {
        return { tagName, attribs };
      }
      // Note the fallback below is not itself bounded: an svg authored with a huge viewBox
      // still gets huge px dimensions, as it always has. Clamping it would distort legitimate
      // wide diagrams, so it stays out of this fix.
      const viewBox = attribs.viewbox;
      if (viewBox) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const [_x, _y, width, height] = viewBox.replace(',', '').split(' ');
          attribs.width = width;
          attribs.height = height;
        } catch {
          // do nothing
        }
      } else {
        // Pick some default size for this SVG, so it isn't humungous.
        attribs.width = '32';
        attribs.height = '32';
      }
      return {
        tagName,
        attribs,
      };
    },
    img: (tagName: string, attribs: Attributes) => {
      attribs.referrerpolicy = 'no-referrer';
      if (allowImageStyles) {
        attribs.class = 'allow-image-styles';

        // Preserve width/height properties _unless_ they're a % as they'll likely break our formatting
        // because we don't preserve their parent properties often...
        if (typeof attribs.height === 'string') {
          // In case of height attribute, if its set to specific value, we replace height with max-height, to preserve image ratio when on mobile or small screen device
          if (!attribs.height.includes('%')) {
            attribs.style = attribs.style
              ? `${attribs.style}; max-height: ${attribs.height}px;`
              : `max-height: ${attribs.height}px;`;
          }
          delete attribs.height;
        }
        // If style attribute includes height, change it to max-height
        if (typeof attribs.style === 'string') {
          if (attribs.style.startsWith('height:')) {
            attribs.style = attribs.style.replace('height:', 'max-height:');
          }
          if (attribs.style.includes(' height:')) {
            attribs.style = attribs.style.replace(' height:', ' max-height:');
          }
          if (attribs.style.includes(';height:')) {
            attribs.style = attribs.style.replace(';height:', ';max-height:');
          }
        }
        if (typeof attribs.width === 'string' && attribs.width.includes('%')) {
          delete attribs.width;
        }
        if (attribs.style?.includes('width: 100%')) {
          attribs.style = attribs.style.replace('width: 100%', '');
        }
        if (attribs.style?.includes('height: 100%')) {
          attribs.style = attribs.style.replace('height: 100%', '');
        }
      }
      // For devs: Uncomment this if you want images to refresh every time
      if (isDevOrTest) {
        // console.log("Don't forget to comment me back out!");
        // const timestamp = new Date().getTime();
        // attribs.src = `${attribs.src}?cache_bust=${timestamp}`;
      }
      return {
        attribs,
        tagName: 'img',
      };
    },
    a: (tagName: string, attribs: Attributes) => {
      const isInternalLink = attribs?.href?.startsWith('#');

      if (!isInternalLink) {
        attribs.target = '_blank';
      }
      attribs.rel = 'noreferrer';
      return {
        attribs,
        tagName: 'a',
      };
    },
  };
}
